/**
 * BootcampQuizFlow — polished, question-by-question quiz-taker for the
 * bootcamp pre-assessment quizzes.
 *
 * Visual pattern taken from the reference screenshots: one question per
 * screen, card-style options for MCQ, big CTA at the bottom, "Question X
 * of Y" header with a thin progress bar, back arrow for previous.
 *
 * States
 * ------
 * - **loading** — fetching the quiz
 * - **taking**  — one question per screen, answers accumulate in memory
 * - **submitting** — POST in flight
 * - **results** — score + pass/fail + per-question review + retry
 * - **error** — toast fired + retry button
 *
 * Scenarios considered
 * --------------------
 * - Empty quiz (no questions) — shows a friendly empty-state inside the modal
 * - Text-type question — swaps card selector for a textarea
 * - Back from Q1 — confirms exit
 * - Submit failure — toast + stays on last question
 * - Retry — resets state, returns to Q1
 * - Keyboard nav via WizardShell (← → Enter, digits for MCQ handled here)
 * - Motion-reduce respected through primitives
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Award,
    Check,
    RotateCcw,
    Target,
    X as XIcon,
} from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import { Skeleton } from '../Skeleton';
import { useToastContext } from '../ToastProvider';
import {
    getQuizDetail,
    submitQuizAttempt,
    type BootcampQuizFull,
    type BootcampQuizAttemptResult,
    type BootcampQuizQuestion,
} from '../../bootcampService';
import WizardShell from './wizard/WizardShell';
import OptionCard from './wizard/OptionCard';
import TextAnswer from './wizard/TextAnswer';
import './BootcampQuizFlow.css';

type Props = {
    quizId: string;
    onClose: () => void;
};

type Phase = 'loading' | 'taking' | 'submitting' | 'results' | 'error';

function BootcampQuizFlow({ quizId, onClose }: Props) {
    const [phase, setPhase] = useState<Phase>('loading');
    const [quiz, setQuiz] = useState<BootcampQuizFull | null>(null);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [currentIdx, setCurrentIdx] = useState(0);
    const [result, setResult] = useState<BootcampQuizAttemptResult | null>(null);
    const { success, error: toastError } = useToastContext();

    // ── Load quiz ─────────────────────────────────────────────────────
    const load = useCallback(async () => {
        setPhase('loading');
        try {
            const data = await getQuizDetail(quizId);
            setQuiz(data);
            setAnswers({});
            setCurrentIdx(0);
            setResult(null);
            setPhase('taking');
        } catch (err) {
            console.error('[BootcampQuizFlow] load failed', err);
            setPhase('error');
        }
    }, [quizId]);

    useEffect(() => {
        // Data-fetch on mount — the React 19 hook linter flags this as
        // "setState inside effect", but it's the canonical pre-Suspense
        // fetch pattern the rest of the codebase uses (see
        // useFetchOnMount). Disabled inline so the signal doesn't turn
        // into noise.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        void load();
    }, [load]);

    // ── Keyboard: 1–9 to pick MCQ option ──────────────────────────────
    useEffect(() => {
        if (phase !== 'taking' || !quiz) return;
        const handler = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
            const q = quiz.questions[currentIdx];
            if (!q || q.type !== 'mcq' || !q.options) return;
            if (/^[1-9]$/.test(e.key)) {
                const idx = parseInt(e.key, 10) - 1;
                const opt = q.options[idx];
                if (opt !== undefined) {
                    e.preventDefault();
                    setAnswers((prev) => ({ ...prev, [q.id]: opt }));
                }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [phase, quiz, currentIdx]);

    // ── Handlers ──────────────────────────────────────────────────────
    const handleAnswer = useCallback((questionId: string, value: string) => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
    }, []);

    const handleBack = useCallback(() => {
        setCurrentIdx((prev) => Math.max(prev - 1, 0));
    }, []);

    const handleExit = useCallback(() => {
        if (phase === 'taking' && Object.keys(answers).length > 0) {
            const ok = window.confirm('Exit the quiz? Your answers will be discarded.');
            if (!ok) return;
        }
        onClose();
    }, [answers, onClose, phase]);

    const handleContinue = useCallback(async () => {
        if (!quiz) return;
        const isLast = currentIdx === quiz.questions.length - 1;
        if (!isLast) {
            setCurrentIdx((prev) => prev + 1);
            return;
        }
        // Submit
        setPhase('submitting');
        try {
            const res = await submitQuizAttempt(quiz.id, answers);
            setResult(res);
            setPhase('results');
            if (res.passed) {
                success(`Passed with ${res.score}%.`);
            }
        } catch (err) {
            console.error('[BootcampQuizFlow] submit failed', err);
            toastError("Couldn't submit your attempt. Please try again.");
            setPhase('taking');
        }
    }, [quiz, currentIdx, answers, success, toastError]);

    const handleRetry = useCallback(() => {
        setAnswers({});
        setCurrentIdx(0);
        setResult(null);
        setPhase('taking');
    }, []);

    // ── Render by phase ───────────────────────────────────────────────
    const currentQuestion: BootcampQuizQuestion | null = useMemo(() => {
        return quiz ? quiz.questions[currentIdx] ?? null : null;
    }, [quiz, currentIdx]);

    const canContinue = useMemo(() => {
        if (!currentQuestion) return false;
        const value = answers[currentQuestion.id];
        if (!value) return false;
        if (currentQuestion.type === 'text') {
            return value.trim().length > 0;
        }
        return true;
    }, [answers, currentQuestion]);

    return (
        <Modal
            open
            onClose={handleExit}
            size="lg"
            ariaLabel={quiz?.title ?? 'Pre-assessment quiz'}
        >
            <div className="bcqf">
                {phase === 'loading' && (
                    <div className="bcqf__loading">
                        <Skeleton width="100%" height="44px" borderRadius="12px" />
                        <Skeleton width="80%" height="28px" borderRadius="6px" />
                        <Skeleton width="100%" height="56px" borderRadius="12px" />
                        <Skeleton width="100%" height="56px" borderRadius="12px" />
                        <Skeleton width="100%" height="56px" borderRadius="12px" />
                    </div>
                )}

                {phase === 'error' && (
                    <EmptyState
                        icon={<XIcon size={26} />}
                        title="Couldn't load the quiz"
                        description="Please try again in a moment."
                        action={
                            <Button variant="secondary" size="md" onClick={load}>
                                Try again
                            </Button>
                        }
                    />
                )}

                {phase === 'submitting' && (
                    <EmptyState
                        icon={<RotateCcw size={26} />}
                        title="Submitting your attempt…"
                        description="Grading your answers."
                    />
                )}

                {(phase === 'taking' || phase === 'results') && quiz && quiz.questions.length === 0 && (
                    <EmptyState
                        icon={<XIcon size={26} />}
                        title="No questions yet"
                        description="This quiz doesn't have any questions."
                        action={
                            <Button variant="secondary" size="md" onClick={onClose}>
                                Close
                            </Button>
                        }
                    />
                )}

                {phase === 'taking' && quiz && currentQuestion && quiz.questions.length > 0 && (
                    <WizardShell
                        step={currentIdx + 1}
                        totalSteps={quiz.questions.length}
                        title={currentQuestion.question}
                        subtitle={
                            currentQuestion.type === 'mcq'
                                ? 'Pick the answer that fits best'
                                : 'Type your answer below'
                        }
                        canContinue={canContinue}
                        onBack={currentIdx > 0 ? handleBack : null}
                        onContinue={handleContinue}
                        onExit={handleExit}
                    >
                        {currentQuestion.type === 'mcq' && currentQuestion.options && (
                            <div className="bcqf__options" role="radiogroup" aria-label="Answer options">
                                {currentQuestion.options.map((option, idx) => (
                                    <OptionCard
                                        key={idx}
                                        leading={String.fromCharCode(65 + idx)}
                                        label={option}
                                        selected={answers[currentQuestion.id] === option}
                                        onSelect={() => handleAnswer(currentQuestion.id, option)}
                                    />
                                ))}
                            </div>
                        )}
                        {currentQuestion.type === 'text' && (
                            <TextAnswer
                                value={answers[currentQuestion.id] ?? ''}
                                onChange={(v) => handleAnswer(currentQuestion.id, v)}
                                placeholder="Type your answer…"
                                rows={4}
                            />
                        )}
                    </WizardShell>
                )}

                {phase === 'results' && quiz && result && (
                    <QuizResults
                        quiz={quiz}
                        result={result}
                        onRetry={handleRetry}
                        onClose={onClose}
                    />
                )}
            </div>
        </Modal>
    );
}

// ── Results screen ──────────────────────────────────────────────────────

type ResultsProps = {
    quiz: BootcampQuizFull;
    result: BootcampQuizAttemptResult;
    onRetry: () => void;
    onClose: () => void;
};

function QuizResults({ quiz, result, onRetry, onClose }: ResultsProps) {
    return (
        <div className="bcqf-results">
            <div className={`bcqf-results__hero bcqf-results__hero--${result.passed ? 'pass' : 'fail'}`}>
                <div className="bcqf-results__icon" aria-hidden="true">
                    {result.passed ? <Award size={52} /> : <Target size={52} />}
                </div>
                <h2 className="bcqf-results__title">
                    {result.passed ? 'Passed!' : 'Keep practising'}
                </h2>
                <div className="bcqf-results__score">
                    <span className="bcqf-results__score-value">{result.score}%</span>
                    <span className="bcqf-results__score-label">Your score</span>
                </div>
                <p className="bcqf-results__message">
                    {result.passed
                        ? `You passed! The passing score is ${result.passing_score}%.`
                        : `You need ${result.passing_score}% to pass. Review the answers below and try again.`}
                </p>
                <div className="bcqf-results__actions">
                    <Button variant="secondary" size="md" onClick={onClose}>
                        Back to week
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        icon={<RotateCcw size={16} />}
                        onClick={onRetry}
                    >
                        Retake quiz
                    </Button>
                </div>
            </div>

            <section className="bcqf-results__review">
                <h3>Review your answers</h3>
                {result.answers_review.map((item, idx) => (
                    <div
                        key={item.question_id}
                        className={`bcqf-results__item bcqf-results__item--${item.is_correct ? 'correct' : 'incorrect'}`}
                    >
                        <div className="bcqf-results__item-head">
                            <span className="bcqf-results__item-num">Q{idx + 1}</span>
                            <span className="bcqf-results__item-badge">
                                {item.is_correct ? <Check size={14} /> : <XIcon size={14} />}
                                {item.is_correct ? 'Correct' : 'Incorrect'}
                            </span>
                        </div>
                        <p className="bcqf-results__item-q">{item.question}</p>
                        <p className="bcqf-results__item-ans">
                            <strong>Your answer:</strong>{' '}
                            <span className={item.is_correct ? 'is-correct' : 'is-incorrect'}>
                                {item.user_answer || '(no answer)'}
                            </span>
                        </p>
                        {!item.is_correct && (
                            <p className="bcqf-results__item-ans">
                                <strong>Correct:</strong> {item.correct_answer}
                            </p>
                        )}
                        {item.explanation && (
                            <p className="bcqf-results__item-explain">
                                <strong>Why:</strong> {item.explanation}
                            </p>
                        )}
                    </div>
                ))}
                <p className="bcqf-results__footnote">{quiz.description}</p>
            </section>
        </div>
    );
}

export default BootcampQuizFlow;
